// AirframeEditor — Module 2 tabbed editor.
//
// Tabs: Identity | Mass & Inertia | Aero | Propulsion | Controls | Sensors
// Verify button runs the canned flight against the /verify endpoint and
// renders the result in a sibling VerifyReport.
//
// All fields use <ParamField> so units, source, confidence, bounds, and
// citation are consistent with Module 1. The editor is stateless — the
// parent owns the airframe doc and hands in `onChange` + `onVerify`.
import { useState } from "react";
import { Download, PlayCircle } from "lucide-react";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/button";
import SpinnerButton from "@/components/SpinnerButton";
import ParamField from "@/components/ParamField";
import CredibilityBadge from "@/components/CredibilityBadge";
import AeroTableEditor from "@/components/airframe/AeroTableEditor";
import VerifyReport from "@/components/airframe/VerifyReport";
import { sim } from "@/api/endpoints";

// Pulls a ParamField-shaped doc out of the airframe tree.
function getParam(af, path) {
    const parts = path.split(".");
    let cursor = af;
    for (const p of parts) {
        if (cursor == null) return null;
        cursor = cursor[p];
    }
    return cursor;
}

function PF({ af, path, label, kind, bounds, onChange, readOnly, description }) {
    const p = getParam(af, path) || {};
    return (
        <ParamField
            label={label}
            kind={kind}
            bounds={bounds || p.bounds}
            value={p.value ?? null}
            source={p.source}
            confidence={p.confidence}
            citation={p.citation}
            description={description}
            readOnly={readOnly}
            onChange={(si, unit) => onChange?.(path, { ...p, value: si, unit, source: p.source || "user", confidence: p.confidence || "provisional" })}
            testId={`airframe-${path.replace(/\./g, "-")}`}
        />
    );
}

export default function AirframeEditor({ airframe, onChange, readOnly = false, onAfterVerify }) {
    const [verifying, setVerifying] = useState(false);
    const [verifyErr, setVerifyErr] = useState(null);

    if (!airframe) return null;

    const handleVerify = async () => {
        setVerifying(true);
        setVerifyErr(null);
        try {
            const fresh = await sim.airframes.verify(airframe.id);
            onAfterVerify?.(fresh);
        } catch (e) {
            setVerifyErr(formatApiError(e, "verify failed"));
        } finally {
            setVerifying(false);
        }
    };

    const handleAeroChange = (coef, table) => {
        const next = { ...(airframe.aero || {}) };
        next[coef] = table;
        onChange?.({ ...airframe, aero: next });
        // Persist the table server-side immediately.
        sim.airframes.aeroTable.upload(airframe.id, {
            coefficient: coef,
            table,
        }).catch(() => {});
    };

    return (
        <div className="space-y-4" data-testid="airframe-editor">
            <header className="flex items-center gap-3 flex-wrap border-b border-ux-border-1 pb-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h2
                            className="text-[15px] font-medium text-ux-text-1 truncate"
                            data-testid="airframe-editor-name"
                        >
                            {airframe.name}
                        </h2>
                        <CredibilityBadge
                            entity={airframe}
                            testId="airframe-editor-tier"
                        />
                    </div>
                    <p className="text-[11px] font-mono text-ux-text-3">
                        {airframe.class} · {airframe.role} · v{airframe.version}
                    </p>
                </div>
                <a
                    href={sim.airframes.exportXmlUrl(airframe.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] text-ux-accent"
                    data-testid="airframe-export-xml"
                >
                    <Download className="w-3.5 h-3.5" />
                    Export JSBSim XML
                </a>
                <SpinnerButton
                    loading={verifying}
                    onClick={handleVerify}
                    testId="airframe-verify-button"
                    disabled={readOnly}
                >
                    <PlayCircle className="w-3.5 h-3.5 mr-1" /> Verify
                </SpinnerButton>
            </header>

            {verifyErr && (
                <p className="text-[12px] text-ux-danger" role="alert" data-testid="airframe-verify-error">
                    {verifyErr}
                </p>
            )}

            <Tabs defaultValue="identity" data-testid="airframe-tabs">
                <TabsList className="bg-ux-bg-2 border-ux-border-1">
                    <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
                    <TabsTrigger value="mass" data-testid="tab-mass">Mass &amp; Inertia</TabsTrigger>
                    <TabsTrigger value="aero" data-testid="tab-aero">Aero</TabsTrigger>
                    <TabsTrigger value="propulsion" data-testid="tab-propulsion">Propulsion</TabsTrigger>
                    <TabsTrigger value="controls" data-testid="tab-controls">Controls</TabsTrigger>
                    <TabsTrigger value="sensors" data-testid="tab-sensors">Sensors</TabsTrigger>
                    <TabsTrigger value="verify" data-testid="tab-verify">Verify</TabsTrigger>
                </TabsList>

                <TabsContent value="identity" className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <PF af={airframe} path="geometry.wing_area" label="Wing area" kind="length" onChange={(p, v) => onChange?.({ ...airframe, geometry: { ...airframe.geometry, wing_area: v } })} readOnly={readOnly} description="Reference planform area (m²)" />
                        <PF af={airframe} path="geometry.wing_span" label="Wing span" kind="length" onChange={(p, v) => onChange?.({ ...airframe, geometry: { ...airframe.geometry, wing_span: v } })} readOnly={readOnly} />
                        <PF af={airframe} path="geometry.mean_aerodynamic_chord" label="Mean aerodynamic chord" kind="length" onChange={(p, v) => onChange?.({ ...airframe, geometry: { ...airframe.geometry, mean_aerodynamic_chord: v } })} readOnly={readOnly} />
                        <PF af={airframe} path="geometry.length" label="Length" kind="length" onChange={(p, v) => onChange?.({ ...airframe, geometry: { ...airframe.geometry, length: v } })} readOnly={readOnly} />
                    </div>
                </TabsContent>

                <TabsContent value="mass" className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <PF af={airframe} path="mass.dry_mass" label="Dry mass" kind="mass" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, dry_mass: v } })} readOnly={readOnly} />
                        <PF af={airframe} path="mass.fuel_or_battery_mass" label="Fuel / battery mass" kind="mass" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, fuel_or_battery_mass: v } })} readOnly={readOnly} />
                        <PF af={airframe} path="mass.inertia.Ixx" label="Ixx" kind="dimensionless" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, inertia: { ...airframe.mass.inertia, Ixx: v } } })} readOnly={readOnly} />
                        <PF af={airframe} path="mass.inertia.Iyy" label="Iyy" kind="dimensionless" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, inertia: { ...airframe.mass.inertia, Iyy: v } } })} readOnly={readOnly} />
                        <PF af={airframe} path="mass.inertia.Izz" label="Izz" kind="dimensionless" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, inertia: { ...airframe.mass.inertia, Izz: v } } })} readOnly={readOnly} />
                        <PF af={airframe} path="mass.inertia.Ixz" label="Ixz" kind="dimensionless" onChange={(p, v) => onChange?.({ ...airframe, mass: { ...airframe.mass, inertia: { ...airframe.mass.inertia, Ixz: v } } })} readOnly={readOnly} />
                    </div>
                    <p className="mt-3 text-[11px] text-ux-text-3 font-mono">
                        CG body: [{(airframe.mass?.cg_body || []).map((v) => Number(v).toFixed(2)).join(", ")}]
                    </p>
                </TabsContent>

                <TabsContent value="aero" className="pt-4 space-y-6">
                    {["CL", "CD", "Cm"].map((coef) => (
                        <div key={coef} className="border border-ux-border-1 rounded p-3 bg-ux-bg-1">
                            <AeroTableEditor
                                coefficient={coef}
                                table={airframe.aero?.[coef]}
                                readOnly={readOnly}
                                onChange={(tbl) => handleAeroChange(coef, tbl)}
                            />
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="propulsion" className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <PF af={airframe} path="propulsion.max_thrust" label="Max thrust" kind="force" onChange={(p, v) => onChange?.({ ...airframe, propulsion: { ...airframe.propulsion, max_thrust: v } })} readOnly={readOnly} />
                        <div className="text-[12px] text-ux-text-2">
                            <span className="font-mono uppercase text-[10px] text-ux-text-3 block mb-1">Type</span>
                            {airframe.propulsion?.type}
                        </div>
                    </div>
                    {airframe.propulsion?.thrust_curve ? (
                        <div className="mt-4 border border-ux-border-1 rounded p-3 bg-ux-bg-1">
                            <AeroTableEditor
                                coefficient="thrust_curve"
                                table={airframe.propulsion.thrust_curve}
                                readOnly
                            />
                        </div>
                    ) : (
                        <p className="mt-3 text-[11px] text-ux-text-3 font-mono" data-testid="propulsion-no-curve">
                            No thrust curve on this airframe — using static max_thrust.
                        </p>
                    )}
                </TabsContent>

                <TabsContent value="controls" className="pt-4">
                    {(airframe.control_surfaces || []).length === 0 ? (
                        <p className="text-[12px] text-ux-text-3" data-testid="controls-empty">
                            No control surfaces declared.
                        </p>
                    ) : (
                        <ul className="space-y-3">
                            {(airframe.control_surfaces || []).map((cs, idx) => (
                                <li
                                    key={cs.name || idx}
                                    className="border border-ux-border-1 rounded p-3 bg-ux-bg-1"
                                    data-testid={`control-surface-${cs.name || idx}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[13px] text-ux-text-1 font-medium">{cs.name}</span>
                                        <span className="text-[10px] font-mono uppercase text-ux-text-3">{cs.axis}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                        <ParamField
                                            label="Deflection limit"
                                            kind="angle"
                                            value={cs.deflection_limit?.value}
                                            source={cs.deflection_limit?.source}
                                            confidence={cs.deflection_limit?.confidence}
                                            readOnly
                                            testId={`cs-deflection-${cs.name || idx}`}
                                        />
                                        <ParamField
                                            label="Rate limit"
                                            kind="angle"
                                            value={cs.rate_limit?.value}
                                            source={cs.rate_limit?.source}
                                            confidence={cs.rate_limit?.confidence}
                                            readOnly
                                            testId={`cs-rate-${cs.name || idx}`}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>

                <TabsContent value="sensors" className="pt-4 space-y-5">
                    {airframe.sensors?.seeker && (
                        <section data-testid="sensor-seeker">
                            <h3 className="text-[12px] font-mono uppercase tracking-wider text-ux-text-3 mb-2">Seeker</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <ParamField label="Detection range" kind="length" value={airframe.sensors.seeker.detection_range?.value} source={airframe.sensors.seeker.detection_range?.source} confidence={airframe.sensors.seeker.detection_range?.confidence} readOnly testId="seeker-detection-range" />
                                <ParamField label="FOV (az)" kind="angle" value={airframe.sensors.seeker.fov_az?.value} source={airframe.sensors.seeker.fov_az?.source} confidence={airframe.sensors.seeker.fov_az?.confidence} readOnly testId="seeker-fov-az" />
                                <ParamField label="FOV (el)" kind="angle" value={airframe.sensors.seeker.fov_el?.value} source={airframe.sensors.seeker.fov_el?.source} confidence={airframe.sensors.seeker.fov_el?.confidence} readOnly testId="seeker-fov-el" />
                            </div>
                        </section>
                    )}
                    {airframe.sensors?.imu && (
                        <section data-testid="sensor-imu">
                            <h3 className="text-[12px] font-mono uppercase tracking-wider text-ux-text-3 mb-2">IMU</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <ParamField label="σ accel" kind="dimensionless" value={airframe.sensors.imu.sigma_accel?.value} readOnly testId="imu-sigma-accel" />
                                <ParamField label="σ gyro" kind="dimensionless" value={airframe.sensors.imu.sigma_gyro?.value} readOnly testId="imu-sigma-gyro" />
                                <ParamField label="bias" kind="dimensionless" value={airframe.sensors.imu.bias?.value} readOnly testId="imu-bias" />
                            </div>
                        </section>
                    )}
                    {!airframe.sensors?.seeker && !airframe.sensors?.imu && (
                        <p className="text-[12px] text-ux-text-3" data-testid="sensors-empty">
                            No sensor suite declared.
                        </p>
                    )}
                </TabsContent>

                <TabsContent value="verify" className="pt-4">
                    <VerifyReport verification={airframe.verification} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
